import React, { useState, useEffect, useRef } from "react";
import { NavDropdown } from "react-bootstrap";
import { Link, useLocation, useHistory } from "react-router-dom";
import { FaPhoneAlt, FaBars, FaTimes } from "react-icons/fa";
import safeHtml from "../../utils/safeHtml";
import UserWayAccessibilityMenu from "../UserWayAccessibilityMenu";
import fetchJsonWithFallback from "../../utils/fetchJsonWithFallback";
import { CDN_INCLUDES, CDN_ASSETS } from "../../utils/cdn";
import localNavigation from "../../data/navigation.json";
import "./index.css";

function Navbar() {
  const location = useLocation();
  const history = useHistory();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navigation, setNavigation] = useState(localNavigation);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);

  const scrollToFilters = () => {
    setMenuOpen(false);
    const navbarHeight = document.querySelector(".site-navbar")?.offsetHeight || 80;
    const doScroll = () => {
      const el = document.getElementById("apply-filters");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
        window.scrollTo({ top, behavior: "smooth" });
      }
    };
    if (location.pathname !== "/") {
      history.push("/#apply-filters");
    } else {
      doScroll();
    }
  };

  const downloadFile = (file) => {
    setMenuOpen(false);
    const url = CDN_ASSETS + encodeURIComponent(file);
    const openInBrowser = () => window.open(url, "_blank");
    // iOS Safari blocks blob downloads — detect and fall back to opening in browser
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) { openInBrowser(); return; }
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = file;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      })
      .catch(openInBrowser);
  };

  useEffect(() => {
    let isMounted = true;
    fetchJsonWithFallback(CDN_INCLUDES + "navigation.json", localNavigation).then((data) => {
      if (isMounted) setNavigation(data);
    });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    const isMobile = () => window.innerWidth < 992;

    function handleScroll() {
      const y = window.scrollY;
      setIsScrolled(y > 4);
      if (y === 0) {
        setIsHidden(false);
      } else if (isMobile()) {
        // mobile: hide on scroll-down, show on scroll-up (matches reference navbarTransition)
        setIsHidden(y > lastScrollY.current);
      } else {
        // desktop: always visible once scrolled (matches reference setNavbar(true) for desktop)
        setIsHidden(false);
      }
      lastScrollY.current = y;
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const { nav, heroTitles } = navigation;
  const isHero = Object.prototype.hasOwnProperty.call(heroTitles, location.pathname);
  const isHome = location.pathname === "/";
  const isTransparent = isHero && !isScrolled;

  return (
    <nav className={`site-navbar ${isTransparent ? "site-navbar--transparent" : "site-navbar--solid"} ${isHidden ? "site-navbar--hidden" : ""}`}>
      <div className="site-navbar__inner">
        {isHome ? (
          <span className="site-navbar__logo">
            <img alt="Marco Management, LLC" src={CDN_ASSETS + "logo-mark.webp"} />
          </span>
        ) : (
          <Link to="/" className="site-navbar__logo" onClick={() => setMenuOpen(false)}>
            <img alt="Marco Management, LLC" src={CDN_ASSETS + "logo-mark.webp"} />
          </Link>
        )}

        <button
          type="button"
          className="site-navbar__toggle"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <div className={`site-navbar__menu ${menuOpen ? "site-navbar__menu--open" : ""}`}>
          {nav.links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`site-navbar__link ${location.pathname === link.to ? "site-navbar__link--active" : ""}`}
              onClick={() => setMenuOpen(false)}
            >
              {safeHtml(link.label)}
            </Link>
          ))}

          <NavDropdown title={safeHtml(nav.applyNow.label)} id="apply-now-dropdown" className="site-navbar__dropdown">
            {nav.applyNow.items.map((item) =>
              item.action === "scrollToFilters" ? (
                <NavDropdown.Item key="online" onClick={scrollToFilters}>
                  {safeHtml(item.label)}
                </NavDropdown.Item>
              ) : (
                <NavDropdown.Item
                  key={item.file}
                  onClick={() => downloadFile(item.file)}
                >
                  {safeHtml(item.label)}
                </NavDropdown.Item>
              )
            )}
          </NavDropdown>

          <Link
            to={(nav.messageUs ?? { to: "/Contact" }).to}
            className={`site-navbar__link ${location.pathname === (nav.messageUs ?? { to: "/Contact" }).to ? "site-navbar__link--active" : ""}`}
            onClick={() => setMenuOpen(false)}
          >
            {safeHtml((nav.messageUs ?? { label: "Message Us" }).label)}
          </Link>

          <NavDropdown
            title={<><FaPhoneAlt /> {safeHtml(nav.callUs.label)}</>}
            id="call-us-dropdown"
            className="site-navbar__dropdown site-navbar__phone-dropdown"
          >
            {(nav.callUs.numbers ?? [{ label: "English", display: "(866) 924-4732", tel: nav.callUs.tel }]).map((n) => (
              <NavDropdown.Item key={n.tel} as="a" href={`tel:${n.tel}`}>
                {n.label} &ndash; {n.display}
              </NavDropdown.Item>
            ))}
          </NavDropdown>

          <div className="site-navbar__userway--mobile">
            <UserWayAccessibilityMenu color="black" width={36} height={36} />
          </div>

          <button className="site-navbar__cta" onClick={scrollToFilters}>
            {safeHtml(nav.cta.label)}
          </button>
        </div>

        <div className="site-navbar__userway--desktop">
          <UserWayAccessibilityMenu color="white" width={40} height={40} />
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
